import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FaCalendarAlt, FaMapMarkerAlt, FaComments, FaEye, FaClock, FaCheckCircle, FaTimesCircle, FaHourglassHalf } from 'react-icons/fa';
import { toast } from 'sonner';

export default function MyRentals() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const [rentals, setRentals] = useState([]);
  const [products, setProducts] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchRentals();
    }
  }, [user]);

  const fetchRentals = async () => {
    try {
      setLoading(true);
      // Récupérer les demandes de location de l'utilisateur
      const response = await axios.get(`${API}/rentals?user_id=${user.id}`);
      const rentalsData = response.data;
      setRentals(rentalsData);

      // Récupérer les infos des produits
      const productIds = [...new Set(rentalsData.map(r => r.product_id))];
      const productsMap = {};
      
      for (const productId of productIds) {
        try {
          const productRes = await axios.get(`${API}/products/${productId}`);
          productsMap[productId] = productRes.data;
        } catch (e) {
          console.error('Error fetching product:', productId);
        }
      }
      setProducts(productsMap);
    } catch (error) {
      console.error('Error fetching rentals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle click on Messages button - find or create conversation
  const handleMessagesClick = async (rental) => {
    try {
      // First, try to find an existing conversation for this rental
      const response = await axios.get(`${API}/rentals/conversations/list?user_id=${user.id}`);
      const conversations = response.data;
      
      // Look for a conversation related to this rental
      const existingConv = conversations.find(conv => 
        conv.rental_id === rental.id || 
        (conv.product_id === rental.product_id && conv.renter_id === user.id)
      );
      
      if (existingConv) {
        // Navigate to the existing conversation
        navigate(`/rentals/conversations/${existingConv.id}`);
      } else {
        // Create a new conversation for this rental
        try {
          const product = products[rental.product_id];
          
          // Get the real owner_id from product or shop
          let ownerId = product?.supplier_id;
          
          // If product has shop_id, try to get shop owner
          if (!ownerId && product?.shop_id) {
            try {
              const shopResponse = await axios.get(`${API}/shops/${product.shop_id}`);
              ownerId = shopResponse.data?.owner_id;
            } catch (e) {
              console.log('Could not fetch shop owner');
            }
          }
          
          // Use rental.owner_id as fallback if it looks like a user ID (UUID format)
          if (!ownerId && rental.owner_id && rental.owner_id.includes('-') && rental.owner_id.length > 20) {
            ownerId = rental.owner_id;
          }
          
          if (!ownerId) {
            toast.error(i18n.language === 'fr' ? 'Impossible de trouver le propriétaire' : 'Cannot find owner');
            navigate('/rentals/conversations');
            return;
          }
          
          // Check if user is trying to message themselves
          if (ownerId === user.id) {
            toast.info(i18n.language === 'fr' ? 'Vous êtes le propriétaire de cet équipement' : 'You are the owner of this equipment');
            navigate('/rentals/conversations');
            return;
          }
          
          const newConvResponse = await axios.post(`${API}/rentals/conversations`, {
            rental_id: rental.id,
            product_id: rental.product_id,
            renter_id: user.id,
            owner_id: ownerId
          });
          
          if (newConvResponse.data?.id) {
            navigate(`/rentals/conversations/${newConvResponse.data.id}`);
          } else {
            // Fallback to conversations list
            navigate('/rentals/conversations');
          }
        } catch (createError) {
          console.error('Error creating conversation:', createError);
          // Check for specific error message
          if (createError.response?.data?.detail) {
            toast.error(createError.response.data.detail);
          } else {
            toast.info(i18n.language === 'fr' ? 'Accédez à vos conversations de location' : 'Access your rental conversations');
          }
          navigate('/rentals/conversations');
        }
      }
    } catch (error) {
      console.error('Error finding conversation:', error);
      // Fallback to conversations list
      navigate('/rentals/conversations');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            <FaHourglassHalf className="mr-1" />
            {i18n.language === 'fr' ? 'En attente' : 'Pending'}
          </Badge>
        );
      case 'confirmed':
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            <FaCheckCircle className="mr-1" />
            {i18n.language === 'fr' ? 'Confirmée' : 'Confirmed'}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            <FaTimesCircle className="mr-1" />
            {i18n.language === 'fr' ? 'Refusée' : 'Rejected'}
          </Badge>
        );
      case 'completed':
        return (
          <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
            <FaCheckCircle className="mr-1" />
            {i18n.language === 'fr' ? 'Terminée' : 'Completed'}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">
            <FaTimesCircle className="mr-1" />
            {i18n.language === 'fr' ? 'Annulée' : 'Cancelled'}
          </Badge>
        );
      default:
        return (
          <Badge className="bg-gray-100 text-gray-600 hover:bg-gray-100">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto"></div>
          <p className="mt-4 text-gray-600">
            {i18n.language === 'fr' ? 'Chargement...' : 'Loading...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">
              {i18n.language === 'fr' ? '📅 Mes Demandes de Location' : '📅 My Rental Requests'}
            </h1>
            <p className="text-gray-600 mt-1">
              {i18n.language === 'fr' 
                ? 'Suivez l\'état de vos demandes de location' 
                : 'Track the status of your rental requests'}
            </p>
          </div>
          <Link to="/rental">
            <Button className="bg-[#00D4FF] hover:bg-[#00B4D8]">
              {i18n.language === 'fr' ? '+ Nouvelle location' : '+ New Rental'}
            </Button>
          </Link>
        </div>

        {rentals.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <div className="text-6xl mb-4">📭</div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                {i18n.language === 'fr' 
                  ? 'Aucune demande de location' 
                  : 'No rental requests'}
              </h2>
              <p className="text-gray-500 mb-6">
                {i18n.language === 'fr'
                  ? 'Vous n\'avez pas encore fait de demande de location.'
                  : 'You haven\'t made any rental requests yet.'}
              </p>
              <Link to="/rental">
                <Button className="bg-[#00D4FF] hover:bg-[#00B4D8]">
                  {i18n.language === 'fr' ? 'Découvrir les équipements à louer' : 'Discover equipment for rent'}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {rentals.map((rental) => {
              const product = products[rental.product_id];
              return (
                <Card key={rental.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Product Image */}
                      <div className="w-full md:w-48 h-32 flex-shrink-0">
                        {product?.images?.[0] ? (
                          <img
                            src={product.images[0]}
                            alt={product?.name_fr}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
                            <span className="text-4xl">🏗️</span>
                          </div>
                        )}
                      </div>

                      {/* Rental Info */}
                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-[#0A2540]">
                              {product 
                                ? (i18n.language === 'fr' ? product.name_fr : product.name_en)
                                : (i18n.language === 'fr' ? 'Produit' : 'Product')
                              }
                            </h3>
                            {product?.location && (
                              <p className="text-sm text-gray-500 flex items-center mt-1">
                                <FaMapMarkerAlt className="mr-1" />
                                {product.location}
                              </p>
                            )}
                          </div>
                          {getStatusBadge(rental.status)}
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                              {i18n.language === 'fr' ? 'Date début' : 'Start Date'}
                            </p>
                            <p className="font-semibold flex items-center">
                              <FaCalendarAlt className="mr-2 text-[#00D4FF]" />
                              {formatDate(rental.start_date)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                              {i18n.language === 'fr' ? 'Date fin' : 'End Date'}
                            </p>
                            <p className="font-semibold flex items-center">
                              <FaCalendarAlt className="mr-2 text-[#635BFF]" />
                              {formatDate(rental.end_date)}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                              {i18n.language === 'fr' ? 'Quantité' : 'Quantity'}
                            </p>
                            <p className="font-semibold">
                              {rental.quantity || 1} {i18n.language === 'fr' ? 'unité(s)' : 'unit(s)'}
                            </p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">
                              {i18n.language === 'fr' ? 'Demandé le' : 'Requested on'}
                            </p>
                            <p className="font-semibold flex items-center">
                              <FaClock className="mr-2 text-gray-400" />
                              {formatDate(rental.created_at)}
                            </p>
                          </div>
                        </div>

                        {/* Message */}
                        {rental.message && (
                          <div className="bg-blue-50 rounded-lg p-3 mb-4">
                            <p className="text-sm text-gray-700">
                              <strong>{i18n.language === 'fr' ? 'Votre message:' : 'Your message:'}</strong> {rental.message}
                            </p>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                          <Link to={`/rental/${rental.product_id}`}>
                            <Button variant="outline" size="sm">
                              <FaEye className="mr-2" />
                              {i18n.language === 'fr' ? 'Voir le produit' : 'View Product'}
                            </Button>
                          </Link>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="text-[#635BFF] border-[#635BFF] hover:bg-[#635BFF]/10"
                            onClick={() => handleMessagesClick(rental)}
                          >
                            <FaComments className="mr-2" />
                            {i18n.language === 'fr' ? 'Messages' : 'Messages'}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Legend */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="text-lg">
              {i18n.language === 'fr' ? '📊 Légende des statuts' : '📊 Status Legend'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Badge className="bg-yellow-100 text-yellow-800">
                  <FaHourglassHalf className="mr-1" />
                  {i18n.language === 'fr' ? 'En attente' : 'Pending'}
                </Badge>
                <span className="text-sm text-gray-600">
                  {i18n.language === 'fr' ? '- En attente de réponse du propriétaire' : '- Waiting for owner response'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-green-100 text-green-800">
                  <FaCheckCircle className="mr-1" />
                  {i18n.language === 'fr' ? 'Confirmée' : 'Confirmed'}
                </Badge>
                <span className="text-sm text-gray-600">
                  {i18n.language === 'fr' ? '- Location acceptée' : '- Rental accepted'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-800">
                  <FaTimesCircle className="mr-1" />
                  {i18n.language === 'fr' ? 'Refusée' : 'Rejected'}
                </Badge>
                <span className="text-sm text-gray-600">
                  {i18n.language === 'fr' ? '- Demande refusée' : '- Request rejected'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
